import { useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Pause, Play, SkipBack, SkipForward, Square } from "lucide-react";
import LiveConfirmDialog from "./LiveConfirmDialog";
import type { ControlButton } from "../../player/controls/playerControlsSpec";
import type { CastLocalId } from "../../lib/castDock";
import { useCastQueueStore } from "../../store/castQueueStore";
import { useOwnCastStore } from "../../store/ownCastStore";

const btn = "w-7 h-7 rounded-md bg-black/45 text-white text-[13px] flex items-center justify-center disabled:opacity-50";

/** "Your file {{n}}" / "Your link": your own cast's dock label. */
export function ownCastLabel(t: TFunction, localId: CastLocalId): string {
  if (localId === "VMPL_LINK_1") return t("dock_label_own_link", "Your link");
  return t("dock_label_own_file", "Your file {{n}}", { n: localId === "VMPL_FILE_2" ? 2 : 1 });
}

export interface OwnCastActions {
  playing: boolean;
  canToggle: boolean;
  /** A link cast with a queue of 2+: previous / next. */
  multi: boolean;
  canStep: boolean;
  label: string;
  toggle(): void;
  step(direction: "next" | "previous"): void;
  /** Stop asks first: opens the confirm. */
  askStop(): void;
  /** The stop confirm while it is open (not portalled: the caller puts it where it shows). */
  confirmDialog: ReactNode | null;
}

/**
 * Your own cast's actions (Android dock play/pause, steps and stop): play/pause through the own-cast store with the
 * player's position now; stop after a confirm. Shared by the dock's buttons and the full-screen controls.
 */
export function useOwnCastActions(localId: CastLocalId, positionMs: () => number): OwnCastActions {
  const { t } = useTranslation();
  const playing = useOwnCastStore((s) => !!s.casts[localId]?.playing);
  // Re-read on every store change (the record is replaced when a resume comes back).
  const canToggle = useOwnCastStore((s) => s.casts[localId] != null && s.canToggle(localId));
  // Subscribes to the queue store as well: a queue that arrives later enables the steps.
  const slotId = useOwnCastStore((s) => s.casts[localId]?.slot.id);
  const multi = useCastQueueStore((s) => localId === "VMPL_LINK_1" && slotId != null && (s.bySlot[slotId]?.queue.count ?? 0) > 1);
  const canStep = useOwnCastStore((s) => s.canStep(localId));
  const [confirming, setConfirming] = useState(false);
  const label = ownCastLabel(t, localId);
  const toggle = () => {
    const store = useOwnCastStore.getState();
    if (!store.canToggle(localId)) return;
    store.togglePlay(localId, positionMs());
  };
  const stop = () => {
    setConfirming(false);
    useOwnCastStore.getState().stop(localId);
  };
  return {
    playing,
    canToggle,
    multi,
    canStep,
    label,
    toggle,
    step: (direction) => useOwnCastStore.getState().step(localId, direction),
    askStop: () => setConfirming(true),
    confirmDialog: confirming ? (
      <LiveConfirmDialog
        message={t("msg_close_stream", "Do you want to close the current streaming?")}
        confirmLabel={t("dock_stop", "Stop")}
        cancelLabel={t("cancel", "Cancel")}
        confirmTestId="own-cast-stop-confirm"
        onConfirm={stop}
        onCancel={() => setConfirming(false)}
      />
    ) : null,
  };
}

/** The same actions as full-screen controls (keeping the dock's tags): previous · play/pause · next, and stop. */
export function ownCastFullScreenButtons(t: TFunction, a: OwnCastActions): { prev?: ControlButton; playPause: ControlButton; next?: ControlButton; stop: ControlButton } {
  const step = (direction: "next" | "previous"): ControlButton => ({
    testId: direction === "next" ? "own-cast-next" : "own-cast-prev",
    label: direction === "next" ? t("dock_next", "Next") : t("dock_previous", "Previous"),
    icon: direction === "next" ? <SkipForward aria-hidden="true" /> : <SkipBack aria-hidden="true" />,
    onClick: () => a.step(direction),
    disabled: !a.canStep,
  });
  return {
    prev: a.multi ? step("previous") : undefined,
    playPause: {
      testId: "own-cast-toggle",
      label: a.playing ? t("dock_pause", "Pause") : t("dock_play", "Play"),
      icon: a.playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />,
      onClick: a.toggle,
      disabled: !a.canToggle,
    },
    next: a.multi ? step("next") : undefined,
    stop: {
      testId: "own-cast-stop",
      label: t("dock_stop_player", "Stop, {{name}}", { name: a.label }),
      icon: <Square aria-hidden="true" />,
      onClick: a.askStop,
      variant: "destructive",
    },
  };
}

/** Your own cast's dock controls: ⏮ play/pause ⏭ ■. */
export default function OwnCastControls({ localId, positionMs }: { localId: CastLocalId; positionMs: () => number }) {
  const { t } = useTranslation();
  const a = useOwnCastActions(localId, positionMs);
  const stopRef = useRef<HTMLButtonElement>(null);
  const stepBtn = (direction: "next" | "previous") => (
    <button
      type="button"
      data-testid={direction === "next" ? "own-cast-next" : "own-cast-prev"}
      onClick={() => a.step(direction)}
      disabled={!a.canStep}
      className={btn}
      title={direction === "next" ? t("dock_next", "Next") : t("dock_previous", "Previous")}
      aria-label={direction === "next" ? t("dock_next", "Next") : t("dock_previous", "Previous")}
    >
      {direction === "next" ? "⏭" : "⏮"}
    </button>
  );

  return (
    <>
      {a.multi && stepBtn("previous")}
      <button
        type="button"
        data-testid="own-cast-toggle"
        onClick={a.toggle}
        disabled={!a.canToggle}
        className={btn}
        title={a.playing ? t("dock_pause", "Pause") : t("dock_play", "Play")}
        aria-label={a.playing ? t("dock_pause", "Pause") : t("dock_play", "Play")}
      >
        {a.playing ? "⏸" : "▶"}
      </button>
      {a.multi && stepBtn("next")}
      <button
        type="button"
        ref={stopRef}
        data-testid="own-cast-stop"
        onClick={a.askStop}
        className={btn}
        title={t("dock_stop_player", "Stop, {{name}}", { name: a.label })}
        aria-label={t("dock_stop_player", "Stop, {{name}}", { name: a.label })}
      >
        ■
      </button>
      {/* Out of the dock: its transform and the card's overflow would trap a fixed overlay. The
          button's own document, so a card popped into a PiP window confirms there. */}
      {a.confirmDialog && createPortal(a.confirmDialog, stopRef.current?.ownerDocument.body ?? document.body)}
    </>
  );
}
